/**
 * One medium, every field of it.
 *
 * The form holds a **draft** and writes it on Save; nothing here is a live write, because a
 * catalog row is a paragraph of prose plus twenty enumerations and saving on each keystroke
 * would put a half-typed tagline in front of a parent.
 *
 * Two things are deliberately *not* the Save button:
 *
 * - **Publishing.** It is its own decision with its own confirm, because it is the only edit
 *   on this page a parent feels immediately. Editing a tagline must never publish anything.
 * - **The illustration.** It is an upload, so it commits when the file lands.
 *
 * `validateMedium` runs on every draft change and its problems show inline. They do **not**
 * block Save — a half-filled new row has to be saveable, or a create is unusable — but they
 * do block Publish, because a row the readers would refuse has no business on a shelf.
 */

import {
  Button,
  Checkbox,
  EmptyState,
  Field,
  SelectField,
  SurfaceHeader,
  TextLink,
} from '@atlas-nook/design-system';
import { InvalidMediumError, normalizeMedium, validateMedium } from '@atlas-nook/mediums';
import type {
  Availability,
  ConsistencyRisk,
  Currency,
  CutterDependency,
  FrameModel,
  Medium,
  MediumFamily,
  NarrativeStrength,
  ProductionEase,
  RefillModel,
  Tier,
  YoungSafe,
} from '@atlas-nook/mediums';
import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';

import { useMedium } from '../data/queries.js';
import { saveMedium } from '../data/writes.js';
import { useToast } from '../shell/ToastProvider.js';
import { ADMIN_STRINGS as S } from '../strings.js';
import { CompositionFields } from './CompositionFields.js';
import { IllustrationSlot } from './IllustrationSlot.js';
import { LocalizedField } from './LocalizedField.js';
import { PhotographsEditor } from './PhotographsEditor.js';
import {
  AVAILABILITY,
  CUTTER,
  entriesOf,
  FAMILY,
  FRAME_MODEL,
  NARRATIVE,
  PRODUCTION_EASE,
  REFILL,
  RISK,
  TIER,
  YOUNG_SAFE,
} from './options.js';
import { PublishControls } from './PublishControls.js';
import { WhatArrivesEditor } from './WhatArrivesEditor.js';

/**
 * What is wrong with this draft, in the readers' own words.
 *
 * `normalizeMedium` throwing and `validateMedium` complaining are different failures:
 * the first means there is nothing to write at all, the second that what would be written is
 * a row the store and the Studio can read but should not show.
 */
export function draftProblems(draft: Medium): { writable: boolean; problems: string[] } {
  try {
    return { writable: true, problems: validateMedium(normalizeMedium(draft)) };
  } catch (error) {
    if (error instanceof InvalidMediumError) return { writable: false, problems: error.problems };
    throw error;
  }
}

export function MediumDetail(): JSX.Element {
  const { id } = useParams();
  const { data: medium, loading } = useMedium(id);

  if (id === undefined) return <Navigate to="/mediums" replace />;
  if (medium === null) {
    return loading ? <></> : <EmptyState title={S.medium.notFound} />;
  }
  // Keyed on the record's identity so a version arriving from another tab resets the draft
  // rather than merging into one someone is mid-way through typing.
  return <MediumForm key={medium.id} medium={medium} />;
}

function MediumForm({ medium }: { medium: Medium }): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [draft, setDraft] = useState<Medium>(medium);
  const [working, setWorking] = useState(false);

  // The published flags are not the form's — they change through their own confirm — so a
  // publish landing while the form is open has to reach the draft, or the next Save would
  // not carry them (it does not write them, but the surface reads them from here).
  useEffect(() => {
    setDraft((was) => ({
      ...was,
      published: medium.published,
      ...(medium.comingSoon === undefined ? {} : { comingSoon: medium.comingSoon }),
      ...(medium.illustrationPath === undefined
        ? {}
        : { illustrationPath: medium.illustrationPath }),
    }));
  }, [medium.published, medium.comingSoon, medium.illustrationPath]);

  const { writable, problems } = draftProblems(draft);

  function set<K extends keyof Medium>(key: K, value: Medium[K]): void {
    setDraft((was) => ({ ...was, [key]: value }));
  }

  async function save(): Promise<void> {
    setWorking(true);
    try {
      await saveMedium(draft);
      // Saved is saved. The problems are still problems, and the count says so rather than
      // pretending the row is finished.
      toast.show(
        problems.length === 0 ? S.common.saved : S.common.savedWithProblems(problems.length),
      );
    } catch {
      toast.show(S.common.failed);
    } finally {
      setWorking(false);
    }
  }

  return (
    <>
      <SurfaceHeader
        kicker={S.medium.kicker}
        title={draft.name.en === '' ? draft.id : draft.name.en}
        meta={S.medium.slugLocked(draft.id)}
        actions={
          <>
            <TextLink onClick={() => void navigate('/mediums')}>{S.nav.catalog}</TextLink>
            <Button
              tone="primary"
              working={working}
              isDisabled={!writable}
              onClick={() => void save()}
            >
              {working ? S.common.saving : S.common.save}
            </Button>
          </>
        }
      />

      {problems.length > 0 && (
        <div className="probs" role="status">
          <p className="note">{S.medium.problemsTitle}</p>
          <ul>
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        </div>
      )}

      <PublishControls medium={medium} blocked={problems.length > 0} />

      <Section title={S.medium.sections.copy}>
        <LocalizedField
          label={S.medium.name}
          required
          value={draft.name}
          onChange={(value) => set('name', value ?? { en: '' })}
        />
        <LocalizedField
          label={S.medium.formatLabel}
          help={S.medium.formatLabelHelp}
          value={draft.formatLabel}
          onChange={(value) => set('formatLabel', value)}
        />
        <LocalizedField
          label={S.medium.tagline}
          value={draft.tagline}
          onChange={(value) => set('tagline', value)}
        />
        <LocalizedField
          label={S.medium.description}
          multiline
          value={draft.description}
          onChange={(value) => set('description', value)}
        />
        <LocalizedField
          label={S.medium.physicalAction}
          help={S.medium.physicalActionHelp}
          required
          value={draft.physicalAction}
          onChange={(value) => set('physicalAction', value ?? { en: '' })}
        />
        <LocalizedField
          label={S.medium.storytellingQuality}
          multiline
          value={draft.storytellingQuality}
          onChange={(value) => set('storytellingQuality', value)}
        />
      </Section>

      <Section title={S.medium.sections.shelf}>
        <div className="frow">
          <SelectField
            label={S.medium.family}
            value={draft.family}
            onChange={(value) => set('family', value as MediumFamily)}
          >
            {entriesOf(FAMILY).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </SelectField>

          <SelectField
            label={S.medium.availability}
            value={draft.availability}
            onChange={(value) => set('availability', value as Availability)}
          >
            {entriesOf(AVAILABILITY).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </SelectField>

          <Field
            label={S.medium.order}
            help={S.medium.orderHelp}
            type="number"
            value={String(draft.order)}
            onChange={(value) => set('order', Number(value) || 0)}
          />
        </div>
        <Checkbox
          label={S.medium.featured}
          checked={draft.featured === true}
          onChange={(on) => set('featured', on ? true : undefined)}
        />
      </Section>

      <Section title={S.medium.sections.audience}>
        <div className="frow">
          <Field
            label={S.medium.ageMin}
            type="number"
            value={String(draft.ageMin)}
            onChange={(value) => set('ageMin', Number(value) || 0)}
          />
          <Field
            label={S.medium.ageMax}
            type="number"
            value={String(draft.ageMax)}
            onChange={(value) => set('ageMax', Number(value) || 0)}
          />
          <SelectField
            label={S.medium.youngSafe}
            help={S.medium.youngSafeHelp}
            value={draft.youngSafe}
            onChange={(value) => set('youngSafe', value as YoungSafe)}
          >
            {entriesOf(YOUNG_SAFE).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </SelectField>
        </div>
      </Section>

      <Section title={S.medium.sections.decision}>
        <div className="frow">
          <SelectField
            label={S.medium.tier}
            help={S.medium.tierHelp}
            value={draft.tier === null ? 'none' : String(draft.tier)}
            onChange={(value) => set('tier', (value === 'none' ? null : Number(value)) as Tier)}
          >
            {entriesOf(TIER).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </SelectField>

          <SelectField
            label={S.medium.productionEase}
            value={draft.productionEase}
            onChange={(value) => set('productionEase', value as ProductionEase)}
          >
            {entriesOf(PRODUCTION_EASE).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </SelectField>

          <SelectField
            label={S.medium.cutterDependency}
            value={draft.cutterDependency}
            onChange={(value) => set('cutterDependency', value as CutterDependency)}
          >
            {entriesOf(CUTTER).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </SelectField>
        </div>

        <div className="frow">
          <SelectField
            label={S.medium.consistencyRisk}
            value={draft.consistencyRisk}
            onChange={(value) => set('consistencyRisk', value as ConsistencyRisk)}
          >
            {entriesOf(RISK).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </SelectField>

          <SelectField
            label={S.medium.refillModel}
            value={draft.refillModel}
            onChange={(value) => set('refillModel', value as RefillModel)}
          >
            {entriesOf(REFILL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </SelectField>

          <SelectField
            label={S.medium.narrativeStrength}
            value={draft.narrativeStrength}
            onChange={(value) => set('narrativeStrength', value as NarrativeStrength)}
          >
            {entriesOf(NARRATIVE).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </SelectField>
        </div>

        <Checkbox
          label={S.medium.ruledOut}
          checked={draft.ruledOut === true}
          onChange={(on) => set('ruledOut', on ? true : undefined)}
        />
        <p className="note">{S.medium.ruledOutHelp}</p>
        <LocalizedField
          label={S.medium.ruledOutReason}
          multiline
          value={draft.ruledOutReason}
          onChange={(value) => set('ruledOutReason', value)}
        />
      </Section>

      <Section title={S.medium.sections.generation}>
        <SelectField
          label={S.medium.frameModel}
          value={draft.frameModel}
          onChange={(value) => set('frameModel', value as FrameModel)}
        >
          {entriesOf(FRAME_MODEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </SelectField>
        <LocalizedField
          label={S.medium.generationNotes}
          multiline
          value={draft.generationNotes}
          onChange={(value) => set('generationNotes', value)}
        />
      </Section>

      <Section title={S.medium.sections.composition}>
        <CompositionFields
          composition={draft.composition}
          onChange={(value) => set('composition', value)}
        />
      </Section>

      <Section title={S.medium.sections.illustration}>
        <IllustrationSlot mediumId={medium.id} path={medium.illustrationPath} />
      </Section>

      <Section title={S.medium.photographs}>
        <PhotographsEditor
          mediumId={medium.id}
          rows={draft.photos}
          onChange={(value) => set('photos', value)}
        />
      </Section>

      <Section title={S.medium.sections.arrives}>
        <WhatArrivesEditor
          rows={draft.whatArrives}
          onChange={(value) => set('whatArrives', value)}
        />
      </Section>

      <Section title={S.medium.sections.commerce}>
        <p className="note">{S.medium.commerceHelp}</p>

        {/*
          Shown only on a published row (PRD 03 §4.4). A draft has nothing to price yet, and a
          price field on an unpublished row invites someone to fill it in and believe the
          keepsake is for sale. Publishing is what asks for these — `publishBlockers` is the
          same rule the Publish control reads.
        */}
        {draft.published === true && (
          <div className="frow">
            <Field
              label={S.medium.price}
              value={draft.price === undefined ? '' : String(draft.price.amount)}
              onChange={(value) => {
                const amount = Number(value);
                set(
                  'price',
                  value.trim() === '' || !Number.isFinite(amount) || amount <= 0
                    ? undefined
                    : { amount, currency: draft.price?.currency ?? 'EUR' },
                );
              }}
            />
            <SelectField
              label={S.medium.currency}
              value={draft.price?.currency ?? 'EUR'}
              onChange={(value) =>
                set(
                  'price',
                  draft.price === undefined
                    ? undefined
                    : { ...draft.price, currency: value as Currency },
                )
              }
            >
              {(['EUR', 'TRY', 'USD'] as const).map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </SelectField>
            <Field
              label={S.medium.shipsIn}
              value={draft.shipsIn ?? ''}
              onChange={(value) => set('shipsIn', value.trim() === '' ? undefined : value)}
            />
            <Field
              label={S.medium.checkoutUrl}
              value={draft.checkoutUrl ?? ''}
              onChange={(value) => set('checkoutUrl', value.trim() === '' ? undefined : value)}
            />
          </div>
        )}

        <div className="frow">
          <Field
            label={S.medium.baseSku}
            value={draft.baseSku ?? ''}
            onChange={(value) => set('baseSku', value.trim() === '' ? undefined : value)}
          />
          <Field
            label={S.medium.refillSku}
            value={draft.refillSku ?? ''}
            onChange={(value) => set('refillSku', value.trim() === '' ? undefined : value)}
          />
        </div>
      </Section>

      <Section title={S.medium.sections.record}>
        <p className="cap">{S.medium.createdBy(medium.createdBy)}</p>
      </Section>
    </>
  );
}

/** A titled block of the form. The design system's `SectionHeader` over a plain stack. */
function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="fsec">
      <div className="panelhead">
        <span className="ph-t">{title}</span>
      </div>
      <div className="stack">{children}</div>
    </section>
  );
}
